import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo, { API_ORIGIN } from "@/components/Seo";
import { articleJsonLd, breadcrumbJsonLd } from "@/lib/seo-data";
import {
  Calendar,
  ArrowLeft,
  Tag,
  ChevronRight,
  BookOpen,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORY_COLORS = {
  Guide: "bg-blue-100 text-blue-700",
  Conseils: "bg-green-100 text-green-700",
  "Que faire ?": "bg-orange-100 text-orange-700",
};

function formatDate(iso) {
  if (!iso) return "";

  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function renderInlineMarkdown(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\)|\*[^*]+\*)/g);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={index} className="text-slate-800 font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }

    if (part.startsWith("*") && part.endsWith("*") && !part.startsWith("**")) {
      return (
        <em key={index} className="italic">
          {part.slice(1, -1)}
        </em>
      );
    }

    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, label, href] = linkMatch;
      const isExternal = href.startsWith("http");

      return (
        <a
          key={index}
          href={href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="text-[#1E3A8A] underline font-medium hover:text-[#0F3D5C]"
        >
          {label}
        </a>
      );
    }

    return part;
  });
}

function renderContent(content = "") {
  const lines = content.split("\n");
  const elements = [];
  let listItems = [];

  const flushList = (keyPrefix) => {
    if (listItems.length === 0) return;

    elements.push(
      <ul key={`ul-${keyPrefix}`} className="list-disc ml-6 mb-5 space-y-2">
        {listItems.map((item, index) => (
          <li key={index} className="text-sm text-slate-600 leading-relaxed">
            {renderInlineMarkdown(item)}
          </li>
        ))}
      </ul>
    );

    listItems = [];
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (
      trimmed === "[AD_A]" ||
      trimmed === "[AD_B]" ||
      trimmed === "[BANNER_1]" ||
      trimmed === "[BANNER_2]"
    ) {
      flushList(index);
      return;
    }

    if (trimmed === "") {
      flushList(index);
      return;
    }

    if (trimmed.startsWith("## ")) {
      flushList(index);
      elements.push(
        <h2
          key={`h2-${index}`}
          className="text-xl font-bold text-slate-800 mt-8 mb-4 font-heading"
        >
          {trimmed.slice(3)}
        </h2>
      );
      return;
    }

    if (trimmed.startsWith("* ")) {
      listItems.push(trimmed.slice(2));
      return;
    }

    flushList(index);

    if (trimmed.startsWith("**") && trimmed.endsWith("**")) {
      elements.push(
        <h3
          key={`h3-${index}`}
          className="text-base font-bold text-slate-800 mt-6 mb-3 font-heading"
        >
          {trimmed.slice(2, -2)}
        </h3>
      );
      return;
    }

    if (trimmed === "---") {
      elements.push(
        <hr key={`hr-${index}`} className="my-8 border-slate-200" />
      );
      return;
    }

    elements.push(
      <p
        key={`p-${index}`}
        className="text-sm text-slate-600 leading-relaxed mb-4"
      >
        {renderInlineMarkdown(line)}
      </p>
    );
  });

  flushList("end");

  return elements;
}

function getRelatedArticles(currentPost, allPosts) {
  if (!currentPost || !Array.isArray(allPosts)) return [];

  const candidates = allPosts.filter((article) => article.slug !== currentPost.slug);

  const sameCategory = candidates.filter(
    (article) => article.category === currentPost.category
  );

  const otherArticles = candidates.filter(
    (article) => article.category !== currentPost.category
  );

  return [...sameCategory, ...otherArticles].slice(0, 3);
}

export default function BlogArticlePage() {
  const { slug } = useParams();

  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    Promise.all([
      axios.get(`${API}/blog/articles/${slug}`),
      axios.get(`${API}/blog/articles`),
    ])
      .then(([postResponse, postsResponse]) => {
        setPost(postResponse.data);
        setAllPosts(postsResponse.data || []);
        setLoading(false);
      })
      .catch(() => {
        setPost(null);
        setAllPosts([]);
        setLoading(false);
      });
  }, [slug]);

  const relatedArticles = useMemo(
    () => getRelatedArticles(post, allPosts),
    [post, allPosts]
  );

  return (
    <div className="min-h-screen flex flex-col page-enter">
      {post && (
        <Seo
          title={post.title}
          description={post.excerpt}
          path={`/blog/${post.slug}`}
          type="article"
          image={`${API_ORIGIN}/api/og/blog/${post.slug}.png`}
          keywords={(post.tags || []).join(", ")}
          jsonLd={[
            articleJsonLd(post),
            breadcrumbJsonLd([
              { name: "Accueil", path: "/" },
              { name: "Blog", path: "/blog" },
              { name: post.title, path: `/blog/${post.slug}` },
            ]),
          ]}
        />
      )}

      <Header />

      <main className="flex-1 py-12 bg-[#F9FAFB]">
        <div className="container-app max-w-3xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au blog
          </Link>

          {loading && (
            <div className="card-base p-8 animate-pulse">
              <div className="h-8 bg-slate-200 rounded mb-4 w-3/4" />
              <div className="h-4 bg-slate-200 rounded mb-6 w-1/4" />
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((item) => (
                  <div key={item} className="h-4 bg-slate-200 rounded" />
                ))}
              </div>
            </div>
          )}

          {!loading && !post && (
            <div className="card-base p-10 text-center">
              <BookOpen className="w-10 h-10 mx-auto mb-4 text-slate-300" />

              <p className="text-slate-500 mb-4">Article introuvable.</p>

              <Link to="/blog" className="btn-primary inline-flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Retour au blog
              </Link>
            </div>
          )}

          {post && (
            <>
              <article className="card-base p-8 sm:p-10" data-testid="blog-article">
                <header className="mb-8 pb-6 border-b border-slate-100">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        CATEGORY_COLORS[post.category] ||
                        "bg-blue-100 text-blue-700"
                      }`}
                    >
                      {post.category}
                    </span>

                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(post.created_at)}
                    </span>

                    <span className="text-xs text-slate-400">
                      par {post.author}
                    </span>
                  </div>

                  <h1 className="text-2xl sm:text-3xl font-extrabold font-heading text-slate-900 mb-4 leading-tight">
                    {post.title}
                  </h1>

                  <p className="text-slate-600 text-base leading-relaxed italic">
                    {post.excerpt}
                  </p>
                </header>

                <div className="prose-custom">{renderContent(post.content)}</div>

                {post.tags?.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                    <Tag className="w-4 h-4 text-slate-400" />

                    {post.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </article>

              <div className="mt-8 card-base p-6 text-center">
                <p className="text-sm text-slate-600 mb-4">
                  Vérifiez un site avant d&apos;acheter
                </p>

                <Link to="/" className="btn-primary inline-flex items-center gap-2">
                  Analyser un site <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {relatedArticles.length > 0 && (
                <div className="mt-12">
                  <h2 className="text-xl font-bold font-heading text-slate-800 mb-6">
                    Articles similaires
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {relatedArticles.map((article) => (
                      <Link
                        key={article.slug}
                        to={`/blog/${article.slug}`}
                        className="card-base p-5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group"
                      >
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full mb-2 inline-block ${
                            CATEGORY_COLORS[article.category] ||
                            "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {article.category}
                        </span>

                        <h3 className="font-bold text-slate-800 text-sm mb-2 group-hover:text-[#1E3A8A] transition-colors">
                          {article.title}
                        </h3>

                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>

                        <div className="flex items-center gap-1 text-xs font-semibold text-[#1E3A8A]">
                          Lire l&apos;article <ChevronRight className="w-3.5 h-3.5" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}